import openXml from 'openxml'
import templateBytes from '@/assets/NakdanDocxTemplateBytes.js'
import Ltxml from 'ltxml'
import {processNikudSingleLineIntoStructure} from 'shared/js/wordBreakup'

function removeAims(text) {
  return text.replace(/([אהוי])ֽ/g, '')
}

const BEFORE = 0, WORD = 1, AFTER = 2
const NIKUD_REGEX = /[\u0591-\u05c7]/g

/**
 * getRunOffsets reads a Word document stored in the state, and returns
 * a list of text runs, with the start and end, given as {word, before/word/after, and offset within the before/after/word}
 * nikud does not count, and neither do newline characters, because they are added by the loading process
 * @param state
 * @param textRunArray
 */
function getRunOffsets(state, textRunArray) {
  // In the process of splitting the words, we lose all information about the original runs, so we need to realign the text afterwards
  // remove nikud and split into words and separators
  const wordsAndSeps = processNikudSingleLineIntoStructure(
    state.originalDocument.text.replace(NIKUD_REGEX, '')
  )
  // if a wordId is greater than or equal to originalWordCount, it's an edited word
  const originalWordCount = wordsAndSeps.filter(token => !token.sep).length
  // indices into the wordsAndSeps structure we're going to process
  let wordAndSepTokenIndex = 0
  let wordAndSepCharIndex = -1
  // count of words only, ignoring separators, used in the app itself
  let wordCounter = 0
  let wordOrSep = wordsAndSeps[0].sep ? BEFORE : WORD
  let textRunIndices = []
  let debug = null
  textRunArray.forEach(textRun => {
    const currentRun = {
      startWordIndex: null,
      startWordOrSep: null,
      startOffset: null,
      // we don't currently need to record the end, since each run takes all the data until the next run begins
      // endWordIndex: null,
      // endWordOrSep: null,
      // endWordOffset: null
    }
    const chars = textRun.value.replace(NIKUD_REGEX, '').split('')
    let startedRun = false
    // walk through each character in the run and figure out which word it belongs to
    // it's not necessary to compare the characters, since they are the same
    // - the only issue is skipping newlines inserted while reading the file
    chars.forEach((c) => {
      do {
        // if we're at the end of a token, move to the next one
        if (wordsAndSeps[wordAndSepTokenIndex].word.length === wordAndSepCharIndex + 1) {
          wordAndSepTokenIndex++
          wordAndSepCharIndex = 0
          // since we're at a new token, figure out the wordOrSep status.
          // Moving to BEFORE requires advancing the counter
          // AFTER is only at the end, and WORD must follow a separator, at which point the counter was already advanced
          if (wordsAndSeps[wordAndSepTokenIndex].sep) {
            // always before, unless this is after the last word
            // wordCounter has not yet been incremented, hence -1
            wordOrSep = (originalWordCount - 1) === wordCounter ? AFTER : BEFORE
            if (wordOrSep === BEFORE && wordAndSepTokenIndex !== 0) {
              wordCounter++
            }
          } else {
            wordOrSep = WORD
          }
        }
        else
          wordAndSepCharIndex++
      } while (c !== wordsAndSeps[wordAndSepTokenIndex].word[wordAndSepCharIndex])
      if (!startedRun) {
        startedRun = true
        currentRun.startWordIndex = wordCounter
        currentRun.startWordOrSep = wordOrSep
        currentRun.startOffset = wordAndSepCharIndex
        debug = wordAndSepTokenIndex
      }
    })
    if (startedRun) {
      textRunIndices.push(currentRun)
      if (process.env.DEBUG) {
        // eslint-disable-next-line no-console
        console.log('textrun: ' + textRun.value)
        // eslint-disable-next-line no-console
        console.log('tokens: ', wordsAndSeps.slice(debug, wordAndSepTokenIndex + 1).map(t => t.word).join(''))
      }
    }
  })
  return { originalOffsets: textRunIndices, originalWordCount }
}
const XElement = Ltxml().XElement
const XAttribute = Ltxml().XAttribute
export function generateWordDoc(state, { keepFormattingOption }) {
  const words = state.allWorkingNikud.wordsArray
  var contents, doc, runArray, tNode
  if (keepFormattingOption &&
    state.originalDocument.source && state.originalDocument.source.type === 'docx') {
    doc = new openXml.OpenXmlPackage(state.originalDocument.source.fileBase64Contents) // contents)
    /* the goal is to align the existing text with the original runs, and replace all of them
     * There are two alignment challenges:
     * - changed text. We have three clues - we can bound alignments at unmodified words, we can guess that newlines match original newlines,
     * and we can run a full synopsis. We skip the synopsis for now. Remaining ambiguities are resolved by putting words at the end of the first run
     * that they might belong to.
     * - words broken between runs - if it's a pre-existing word, it's easy - count letters and break at the same point. For new words, give up for now and put in
     * the first run
     * What data do we need to deal with these? In order to align an unchanged word, we can compare the `wordId` to the original number of words
     * The catch is separators - these are regular words from this perspective, but they don't have a wordId. Instead, we can synthesize a wordId. sepBefore = wordId - 0.5, sepAfter (only appears initially at the end) = wordId + 0.1. Newlines need to be removed from separators.
    */
    // we need to keep position in the original words, the current words (possibly after editing), and the runs
    // since we effectively edit runs, we loop over the runs and retain an index into the others
    const textRunArray = doc.mainDocumentPart().getXDocument().descendants(openXml.W.t).toArray()
    const { originalOffsets, originalWordCount } = getRunOffsets(state, textRunArray)
    let prevWordIndex = 0
    textRunArray.forEach((textRun, index) => {
      // console.log("Original run: ", textRun.value)
      const runOffsets = originalOffsets[index]
      if (!runOffsets) {
        // this can occur if the last run was originally empty
        textRun.value = ''
        return
      }
      const nextRunOffsets = originalOffsets[index + 1]
      // console.log(`Generating from ${runOffsets.startWordIndex}[${runOffsets.startWordOrSep}][${runOffsets.startOffset}]`)
      // if (nextRunOffsets)
      //   console.log(`Next run starts ${nextRunOffsets.startWordIndex}[${nextRunOffsets.startWordOrSep}][${nextRunOffsets.startOffset}]`)

      // first collect all the tokens into an array
      let outArray = []
      // endWord is the last word INCLUDED in this run
      const endWord = nextRunOffsets
        ? nextRunOffsets.startWordIndex
        : originalWordCount
      // console.log(`Through ${endWord}`)
      // blindly add them, and we will correct whether to include separators afterwards
      let i
      for(i = prevWordIndex; i < words.length && (words[i].wordId <= endWord || words[i].wordId >= originalWordCount); i++) {
        let word = words[i]
        outArray = outArray.concat([word.sepBefore, word.chosenWordString, word.sepAfter])
      }
      // console.log("Before trimming: " + outArray.join('~'))
      prevWordIndex = i - 1
      // console.log(`End of Run. From last prevWordIndex to ${i - 1}. Set next to start at ${prevWordIndex}`)
      // console.log(nextRunOffsets)
      // remove 0,1, or 2 elements from the beginning, depending on which are supposed to be in this run
      outArray = outArray.slice(runOffsets.startWordOrSep)

      function countIgnoringNikudAndNewlines(outstring, offset) {
        let charCounter = 0, index = 0
        while (charCounter < offset && index < outstring.length) {
          index++
          if (!/[\u0591-\u05c7\n]/.test(outstring[index])) {
            charCounter++
          }
        }
        // console.log(`Slicing here: (${outstring.substr(0, index)}):${outstring.substring(index)}`)
        return index
      }

      // remove 0,1, or 2 elements from the end, depending on which are supposed to be in this run
      // remove the end first - we may slice the same element twice e.g. if a word is split into 3 runs, when processing the middle run
      if (nextRunOffsets) {
        // console.log("Going from " + outArray.length + " to " + (outArray.length - 2 + nextRunOffsets.startWordOrSep))
        outArray = outArray.slice(0, outArray.length - 2 + nextRunOffsets.startWordOrSep)

        // remove chars from the end of the last element
        const last = outArray.length - 1
        if (outArray.length > 0)
          outArray[last] = outArray[last]
            .substr(0, countIgnoringNikudAndNewlines(outArray[last], nextRunOffsets.startOffset))
      }

      // remove the correct number of characters from the first element, but the new first element might have a different amount of nikud
      if (outArray.length > 0)
        outArray[0] = outArray[0].substr(countIgnoringNikudAndNewlines(outArray[0], runOffsets.startOffset))

      // console.log("After trimming: " + outArray.join('~'))
      const newText = removeAims(outArray.join(''))
      if (newText.startsWith(' ') || newText.endsWith(' ')) {
        textRun.setAttributeValue('{http://www.w3.org/XML/1998/namespace}space', 'preserve')
      }
      textRun.value = newText
    })
  } else {
    // contents = base64js.fromByteArray(JSON.parse('['+templateBytes+']'))
    contents = templateBytes
    // console.log(contents)
    doc = new openXml.OpenXmlPackage(contents)
    const allText = words
      .map(word => word.sepBefore + removeAims(word.chosenWordString) + word.sepAfter)
      .join('')
      .split('\n')
    allText.forEach((oneParOfText, li) => {
      if (li === 0) {
        runArray = doc.mainDocumentPart().getXDocument().descendants(openXml.W.r).toArray()
        tNode = runArray[0].descendants(openXml.W.t).toArray()[0] // the template has one paragraph
        tNode.value = oneParOfText
      } else {
        // Create a paragraph.
        var p = new XElement(openXml.W.p,
          new XElement(openXml.W.pPr,
            new XElement(openXml.W.bidi),
            new XElement(openXml.W.rPr,
              new XElement(openXml.W.rFonts,
                new XAttribute(openXml.W.hint, 'cs')),
              new XElement(openXml.W.lang,
                new XAttribute(openXml.W.bidi, 'he-IL')))),
          new XElement(openXml.W.r,
            new XElement(openXml.W.rPr,
              new XElement(openXml.W.rFonts,
                new XAttribute(openXml.W.hint, 'cs')),
              new XElement(openXml.W.rtl),
              new XElement(openXml.W.lang,
                new XAttribute(openXml.W.bidi, 'he-IL'))),
            new XElement(openXml.W.t, oneParOfText)))
        // Append the paragraph to the document.
        var body = doc.mainDocumentPart().getXDocument().root.element(openXml.W.body)
        var lastPara = body.elements(openXml.W.p).lastOrDefault()
        if (lastPara !== null) {
          lastPara.addAfterSelf(p)
        } // else console.log('error - doc reported "last paragraph" to be "null"!')
      }
    })
  }
  return doc.saveToBase64()
}
